import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  BlockIcon,
  CropIcon,
  JpgIcon,
  PenIcon,
  SunIcon,
  UploadIcon,
} from '@/presentation/components/icon/custom-icon';
import Illustration from '@/presentation/components/illustration';
import Loading from '@/presentation/components/loading';

import { EssayViewModel } from '../../essay.vm';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;

  @media (max-width: 640px) {
    padding: 0;
    align-items: end;
  }
`;

const ModalContent = styled.div`
  position: relative;
  background: #333;
  padding: 32px;
  border-radius: 32px;
  max-width: 859px;
  width: 100%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  height: 100vh;
  max-height: 556px;

  @media (max-width: 768px) {
    width: 100%;
    border-radius: 0;
    max-height: unset;
  }
`;

const CloseButton = styled.button`
  font-size: 50px;
  line-height: 1;
  position: absolute;
  padding: 20px;
  right: 0;
  top: 0;
`;

const ModalEssayContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const ModalTitle = styled.h2`
  font-family: Aprova Nova;
  font-size: 32px;
  font-weight: 700;
  line-height: 36px;
  text-align: center;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-top: 24px;
  }
`;

const TipsTitle = styled.h4`
  font-family: Aprova Nova;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.01em;
  text-align: left;
`;

const TipsDescription = styled.h5`
  font-family: Aprova Nova;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.01em;
  text-align: left;
  color: #999999;
`;

const LoadingTitle = styled.h4`
  font-family: Aprova Nova;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.01em;
  text-align: left;
  margin-top: 8px;
`;

const LoadingDescription = styled.h5`
  font-family: Aprova Nova;
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.01em;
  text-align: left;
  color: #999999;
`;

const ModalEssayContentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  @media (max-width: 768px) {
    #illustration {
      display: none;
    }
  }
`;

const ModalEssayTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 8px;
  max-width: 356px;
  margin-left: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 8px;
  max-width: 356px;
  margin-left: 8px;
`;

const TipItem = styled.div`
  display: flex;
  height: 100%;

  svg {
    width: 24px;
    height: 24px;
    margin-right: 8px;
    margin-top: 4px;
  }
`;

export const DetailsButton = styled.button<{ disabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 28px 20px 32px;
  border-radius: 300px;
  margin: 16px 16px 0 0;
  align-self: flex-end;

  color: ${(props) => (!props.disabled ? `#191a1a` : `#636769`)};
  background-color: ${(props) => (!props.disabled ? `#00f99d` : `#f2f2f3`)};

  span {
    font-weight: 700;
    font-size: 20px;
    line-height: 14px;
    margin-left: 8px;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  path {
    fill: black;
    max-width: 14px;
    max-height: 14px;
  }

  @media (max-width: 768px) {
    margin: unset;
    padding: 20px 14px 20px 16px;
  }
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const essayMediaTipsText = [
  {
    icon: <SunIcon />,
    title: 'Boa iluminação',
    description: 'Certifique-se de que a foto está bem iluminada e sem sombras.',
  },
  {
    icon: <PenIcon />,
    title: 'Foco na escrita',
    description: 'Garanta que o texto está nítido e legível.',
  },
  {
    icon: <CropIcon />,
    title: 'Enquadramento',
    description: 'Capture toda a página sem cortar as bordas.',
  },
  {
    icon: <BlockIcon />,
    title: 'Sem reflexos',
    description: 'Evite luzes fortes que possam criar reflexos no papel.',
  },
  {
    icon: <JpgIcon />,
    title: 'Formato correto',
    description: 'Envie em .JPG ou .JPEG, sendo uma imagem clara em alta resolução.',
  },
];

const EssayMediaModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const vm = useViewModel(EssayViewModel);
  const navigate = useNavigate();

  const imageMimeType = /image\/(png|jpg|jpeg)/i;
  const hiddenFileInput = React.useRef(null);

  const changeHandler = async (e) => {
    vm.setIsTranscribing(true);
    const file = e.target.files[0];
    if (!file.type.match(imageMimeType)) {
      alert('Tipo de arquivo não suportado. apenas .png, .jpg ou .jpeg podem ser usados como capa');

      return;
    }
    await vm.setEssayMediaFile(file);
    await vm.transcribeEssayMedia();

    navigate('/essays/write');
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <CloseButton type="button" onClick={onClose}>
          &times;
        </CloseButton>

        <ModalEssayContent>
          {vm.isTranscribing ? (
            <LoadingContainer>
              <Loading />
              <LoadingTitle>{'Transcrevendo sua redação...'}</LoadingTitle>
              <LoadingDescription>{'Esse processo pode demorar um pouco'}</LoadingDescription>
            </LoadingContainer>
          ) : (
            <>
              <ModalTitle>Dicas para enviar uma boa imagem de redação</ModalTitle>
              <ModalEssayContentContainer>
                <Illustration name={'cellphone-photo'} id={'illustration'} />
                <ModalEssayTextContainer>
                  {essayMediaTipsText.map((tips, i) => (
                    <TipItem key={i}>
                      {tips.icon}
                      <div>
                        <TipsTitle>{tips.title}</TipsTitle>
                        <TipsDescription>{tips.description}</TipsDescription>
                      </div>
                    </TipItem>
                  ))}
                </ModalEssayTextContainer>
              </ModalEssayContentContainer>
              <input
                type="file"
                name="thumbnail"
                id="thumbnail"
                accept="image/*"
                capture
                onChange={(e) => {
                  changeHandler(e);
                }}
                ref={hiddenFileInput}
                style={{ display: 'none' }}
              />
              <DetailsButton
                disabled={false}
                type="button"
                onClick={() => {
                  hiddenFileInput.current.click();
                }}
              >
                <UploadIcon />
                <span>Enviar imagem da redação</span>
              </DetailsButton>
            </>
          )}
        </ModalEssayContent>
      </ModalContent>
    </ModalOverlay>
  );
};

export default observer(EssayMediaModal);
